import { CustomDataGrid } from '@/components/CustomDataGrid/CustomDataGrid.tsx';
import { useAuditLog } from '@/contexts/AuditLogContext.tsx';
import { AuthContext } from '@/contexts/AuthContext.tsx';
import { SnackbarContext } from '@/contexts/SnackbarContext.tsx';
import { UserContext } from '@/contexts/UserContext.tsx';
import AddMemberModal from '@/modals/AddMemberModal.tsx';
import { removeMember as removeMemberApi } from '@/services/TeamMembersService.ts';
import { capitalize, emptyConverter } from '@/utils';
import { track } from '@/utils/analytics.ts';
import * as Sentry from '@sentry/react';
import { TeamMember } from '@superfunnel/types';
import Badge from '@superfunnel/ui-core/v2/Badge';
import Button from '@superfunnel/ui-core/v2/Button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@superfunnel/ui-core/v2/DropdownMenu';
import { ColumnDef } from '@tanstack/react-table';
import {
    collection,
    onSnapshot,
    orderBy,
    query,
    where,
} from 'firebase/firestore';
import { EditPencil, MoreVert, Plus, Trash } from 'iconoir-react';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

export const MembersSettings = () => {
  const { isSupportAdmin } = useContext(AuthContext);
  const { selectedAccount, role } = useContext(UserContext);
  const auditLog = useAuditLog();
  const { show } = useContext(SnackbarContext);
  const [members, setMembers] = useState<TeamMember[]>();

  const [selectedMember, setSelectedMember] = useState<TeamMember>();
  const [addMemberModalOpen, setAddMemberModalOpen] = useState(false);

  const canEditMembers = useMemo(() => {
    return isSupportAdmin || role === 'admin';
  }, [isSupportAdmin, role]);

  useEffect(() => {
    if (!selectedAccount) {
      setMembers(undefined);
      return;
    }

    const teamColRef = collection(selectedAccount.ref, 'team').withConverter(
      emptyConverter<TeamMember>(),
    );

    const unsubscribe = onSnapshot(
      query(
        teamColRef,
        where('status', 'in', ['pending', 'approved']),
        orderBy('createdAt', 'asc'),
      ),
      (members) => {
        setMembers(members.docs.map((d) => d.data()));
      },
    );

    return () => {
      unsubscribe();
    };
  }, [selectedAccount]);

  const removeMember = useCallback(
    async (memberId: string) => {
      track('remove_member_button_click', { memberId });

      show('Removing member...');

      try {
        await removeMemberApi(selectedAccount?.id ?? '', memberId);
        show('Member removed');
        auditLog.log('delete', 'Delete team member', 'team', memberId);
      } catch (e: any) {
        Sentry.captureException(e);
        show('Failed to remove member', e);
      }
    },
    [auditLog, selectedAccount?.id, show],
  );

  const columns: ColumnDef<TeamMember>[] = useMemo(() => {
    return [
      {
        header: 'Email',
        id: 'email',
        accessorKey: 'email',
        size: 300,
      },
      {
        header: 'Role',
        id: 'role',
        accessorKey: 'role',
        size: 150,
        cell: (info) => capitalize(info.row.original.role),
      },
      {
        header: 'Status',
        id: 'status',
        accessorKey: 'status',
        size: 150,
        cell: (info) => (
          <Badge variant={'success'}>
            {capitalize(info.row.original.status)}
          </Badge>
        ),
      },
      {
        header: 'Created At',
        id: 'createdAt',
        accessorFn: (row) => row.createdAt?.toDate(),
        size: 200,
        cell: (info) => info.getValue()?.toLocaleString(),
      },
      ...(canEditMembers
        ? ([
            {
              header: '',
              id: 'actions',
              size: 100,
              cell: (info) => {
                const member = info.row.original;
                return (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        size={'icon'}
                        type={'button'}
                        variant={'outline'}
                        className={'rounded-2lg'}
                      >
                        <MoreVert
                          strokeWidth={3}
                          className="h-4 w-4"
                          width={20}
                          height={20}
                        />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent side={'bottom'} align={'end'}>
                      <DropdownMenuItem
                        onClick={() => {
                          setSelectedMember(member);
                          setAddMemberModalOpen(true);
                        }}
                      >
                        <EditPencil className="h-4 w-4" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        variant={'destructive'}
                        onClick={() => removeMember(member.userId)}
                      >
                        <Trash className="h-4 w-4" />
                        Remove
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                );
              },
            },
          ] satisfies ColumnDef<TeamMember>[])
        : []),
    ];
  }, [canEditMembers, removeMember]);
  return (
    <div className="flex h-full flex-col">
      <AddMemberModal
        member={selectedMember}
        open={addMemberModalOpen}
        onClose={() => {
          setAddMemberModalOpen(false);
          setSelectedMember(undefined);
        }}
      />
      <div className="pb-8 sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-base leading-6 font-semibold text-gray-900">
            Members
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            Manage team members, invite new ones, being alone is not fun!
          </p>
        </div>
        {canEditMembers && (
          <Button
            onClick={() => {
              setAddMemberModalOpen(true);
            }}
          >
            <Plus className="h-4.5 w-4.5 text-white" />
            Add Member
          </Button>
        )}
      </div>

      {members?.length === 0 && (
        <p className="mt-3 text-sm text-gray-500">
          You haven’t added any team members to your project yet. As the admin
          of this project, you can manage team member permissions.
        </p>
      )}
      {members?.length !== 0 && (
        <div className="relative flex-1">
          <CustomDataGrid
            logicalName="Members"
            columns={columns}
            data={members}
            rowClassName={'min-h-16'}
            initialState={{
              columnPinning: { right: ['actions'] },
            }}
          />
        </div>
      )}
    </div>
  );
};
